import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const GRANULARITIES = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

// Format a YYYY-MM-DD bucket label for the x-axis based on granularity.
const formatTick = (value, granularity) => {
  if (!value) return '';
  const [y, m, d] = value.split('-').map((n) => parseInt(n, 10));
  const date = new Date(y, (m || 1) - 1, d || 1);
  if (granularity === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short' });
  }
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * Reusable engagement panel for a single feature (Simulations, Manager Intel).
 * Shows headline totals, signed-up vs anonymous conversion, a daily/weekly/
 * monthly trend chart, and a "who did it" table.
 *
 * Props:
 *   accent        – accent color
 *   fetchStats    – (granularity) => axios promise resolving to { series, totals, runners, recentRunners }
 *   runWord       – noun for an individual run (e.g. "simulations", "profiles")
 *   runnerWord    – noun for a distinct person (e.g. "simulators", "managers")
 *   mapRunner     – (row) => { id, title, subtitle, signedUp, runs, lastRunAt }
 */
const EngagementView = ({
  accent = '#3B82F6',
  fetchStats,
  runWord = 'runs',
  runnerWord = 'people',
  mapRunner,
}) => {
  const [granularity, setGranularity] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (g) => {
    try {
      setLoading(true);
      const res = await fetchStats(g);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch engagement stats:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  useEffect(() => {
    load(granularity);
  }, [granularity, load]);

  if (loading && !data) {
    return (
      <Box sx={{ textAlign: 'center', padding: '48px', color: '#94A3B8' }}>
        Loading {runWord}…
      </Box>
    );
  }
  if (!data) return null;

  const { totals, runners, series, recentRunners = [] } = data;
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const cards = [
    { label: 'Today', value: totals.today, sub: `Yesterday: ${totals.yesterday}`, color: accent },
    { label: 'Last 7 days', value: totals.last7Days, sub: cap(runWord), color: '#8B5CF6' },
    { label: 'Last 30 days', value: totals.last30Days, sub: cap(runWord), color: '#06B6D4' },
    { label: 'All time', value: totals.allTime, sub: cap(runWord), color: '#10B981' },
  ];

  return (
    <Box>
      {/* Headline totals */}
      <Grid container spacing={3} sx={{ marginBottom: '20px' }}>
        {cards.map((c) => (
          <Grid item xs={6} md={3} key={c.label}>
            <Box sx={cardSx(c.color)}>
              <Box sx={{ fontSize: '40px', fontWeight: 700, color: c.color }}>{c.value ?? 0}</Box>
              <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', marginTop: '4px' }}>
                {c.label}
              </Box>
              <Box sx={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>{c.sub}</Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Conversion: signed-up vs anonymous */}
      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        <Grid item xs={12} md={4}>
          <Box sx={cardSx('#3B82F6')}>
            <Box sx={{ fontSize: '40px', fontWeight: 700, color: '#3B82F6' }}>{runners.total}</Box>
            <Box sx={labelSx}>Unique {runnerWord}</Box>
          </Box>
        </Grid>
        <Grid item xs={6} md={4}>
          <Box sx={cardSx('#10B981')}>
            <Box sx={{ fontSize: '40px', fontWeight: 700, color: '#10B981' }}>{runners.signedUp}</Box>
            <Box sx={labelSx}>Signed-up users</Box>
            <Box sx={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>{runners.conversionRate}% converted</Box>
          </Box>
        </Grid>
        <Grid item xs={6} md={4}>
          <Box sx={cardSx('#64748B')}>
            <Box sx={{ fontSize: '40px', fontWeight: 700, color: '#94A3B8' }}>{runners.anonymous}</Box>
            <Box sx={labelSx}>Anonymous</Box>
            <Box sx={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>not signed up</Box>
          </Box>
        </Grid>
      </Grid>

      {/* Trend chart with granularity toggle */}
      <Box sx={{ ...panelSx, marginBottom: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <Box sx={{ fontSize: '18px', fontWeight: 700, color: accent }}>
            {cap(runWord)} over time
          </Box>
          <Box sx={{ display: 'flex', gap: '6px', background: '#0F172A', borderRadius: '10px', padding: '4px' }}>
            {GRANULARITIES.map((g) => (
              <Box
                key={g.key}
                onClick={() => setGranularity(g.key)}
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '6px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.15s',
                  color: granularity === g.key ? '#FFF' : '#94A3B8',
                  background: granularity === g.key ? accent : 'transparent',
                  '&:hover': { color: granularity === g.key ? '#FFF' : '#F1F5F9' },
                }}
              >
                {g.label}
              </Box>
            ))}
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis
              dataKey="period"
              stroke="#64748B"
              style={{ fontSize: '12px' }}
              tickFormatter={(v) => formatTick(v, granularity)}
            />
            <YAxis stroke="#64748B" style={{ fontSize: '12px' }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#F1F5F9' }}
              labelFormatter={(v) => formatTick(v, granularity)}
            />
            <Legend wrapperStyle={{ color: '#94A3B8', fontSize: '12px' }} />
            <Bar dataKey="runs" name={cap(runWord)} fill={accent} radius={[4, 4, 0, 0]} maxBarSize={42} />
            <Bar dataKey="uniqueRunners" name={`Unique ${runnerWord}`} fill="#475569" radius={[4, 4, 0, 0]} maxBarSize={42} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Who did it */}
      <Box sx={panelSx}>
        <Box sx={{ fontSize: '18px', fontWeight: 700, color: '#F1F5F9', marginBottom: '16px' }}>
          Who ran {runWord}
          <Box component="span" sx={{ fontSize: '13px', fontWeight: 500, color: '#64748B', marginLeft: '10px' }}>
            most recent {recentRunners.length}
          </Box>
        </Box>
        {recentRunners.length === 0 ? (
          <Box sx={{ fontSize: '13px', color: '#64748B', textAlign: 'center', padding: '24px' }}>
            No {runWord} recorded yet
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '520px', overflow: 'auto' }}>
            {recentRunners.map((row, i) => {
              const r = mapRunner(row);
              return (
                <Box key={r.id || i} sx={rowSx}>
                  <Box sx={{ width: '28px', fontSize: '12px', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
                    {i + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#F1F5F9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.title}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '10px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          padding: '2px 8px',
                          borderRadius: '980px',
                          flexShrink: 0,
                          color: r.signedUp ? '#10B981' : '#94A3B8',
                          background: r.signedUp ? 'rgba(16,185,129,0.12)' : 'rgba(148,163,184,0.12)',
                        }}
                      >
                        {r.signedUp ? 'User' : 'Anon'}
                      </Box>
                    </Box>
                    {r.subtitle && (
                      <Box sx={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.subtitle}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Box sx={{ fontSize: '18px', fontWeight: 700, color: accent }}>{r.runs}</Box>
                    <Box sx={{ fontSize: '10px', color: '#64748B', textTransform: 'uppercase' }}>{runWord}</Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const cardSx = (color) => ({
  background: '#1E293B',
  border: `2px solid ${color}`,
  borderRadius: '12px',
  padding: '20px',
  textAlign: 'center',
  transition: 'all 0.2s',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${color}33` },
});

const labelSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#94A3B8',
  textTransform: 'uppercase',
  marginTop: '4px',
};

const panelSx = {
  background: '#1E293B',
  border: '1px solid #334155',
  borderRadius: '12px',
  padding: '24px',
};

const rowSx = {
  background: '#0F172A',
  border: '1px solid #334155',
  borderRadius: '8px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

export default EngagementView;
